import React from "react";

/**
 * Stylized world map with animated trade route arcs.
 * Uses a simplified SVG world silhouette with port markers for
 * Indonesia, Türkiye, Singapore + global trading hubs.
 */
const HUBS = [
  { id: "id", name: "Jakarta", x: 760, y: 320, primary: true },
  { id: "tr", name: "Istanbul", x: 540, y: 200, primary: true },
  { id: "sg", name: "Singapore", x: 740, y: 295, primary: true },
  { id: "cn", name: "Shanghai", x: 800, y: 240, primary: false },
  { id: "ae", name: "Dubai", x: 600, y: 240, primary: false },
  { id: "nl", name: "Rotterdam", x: 478, y: 170, primary: false },
  { id: "us", name: "Los Angeles", x: 175, y: 240, primary: false },
  { id: "br", name: "Santos", x: 320, y: 380, primary: false },
  { id: "za", name: "Cape Town", x: 555, y: 410, primary: false },
  { id: "in", name: "Mumbai", x: 670, y: 270, primary: false },
];

const ROUTES = [
  ["id", "tr"], ["id", "sg"], ["sg", "cn"], ["tr", "ae"],
  ["tr", "nl"], ["sg", "us"], ["id", "in"], ["id", "za"],
  ["sg", "br"], ["tr", "in"],
];

const arcPath = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  // arc curvature
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const curve = Math.min(120, dist * 0.35);
  return `M ${a.x} ${a.y} Q ${mx} ${my - curve} ${b.x} ${b.y}`;
};

const WorldMap = () => {
  return (
    <div className="relative w-full" aria-hidden="true">
      <svg
        viewBox="0 0 1000 500"
        className="w-full h-auto"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="bgGrad" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#1F2A44" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#0A1128" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="arcGrad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0" />
            <stop offset="50%" stopColor="#D4AF37" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* dotted world (simplified continents using dots) */}
        <g fill="#94A3B8" opacity="0.18">
          {Array.from({ length: 1500 }).map((_, i) => {
            const x = (i * 137) % 1000;
            const y = (i * 53) % 500;
            // Land mass approximation (rough continents)
            const inLand =
              // North America
              (x > 100 && x < 280 && y > 130 && y < 260) ||
              // South America
              (x > 250 && x < 360 && y > 280 && y < 440) ||
              // Europe
              (x > 450 && x < 560 && y > 130 && y < 230) ||
              // Africa
              (x > 480 && x < 600 && y > 230 && y < 430) ||
              // Asia
              (x > 560 && x < 850 && y > 130 && y < 320) ||
              // Australia
              (x > 770 && x < 880 && y > 350 && y < 420);
            if (!inLand) return null;
            return <circle key={i} cx={x} cy={y} r="1.2" />;
          })}
        </g>

        <rect width="1000" height="500" fill="url(#bgGrad)" />

        {/* Routes */}
        <g stroke="url(#arcGrad)" strokeWidth="1.5" fill="none">
          {ROUTES.map(([a, b], idx) => {
            const A = HUBS.find((h) => h.id === a);
            const B = HUBS.find((h) => h.id === b);
            if (!A || !B) return null;
            return (
              <g key={idx}>
                <path d={arcPath(A, B)} className="pulse-arc" style={{ animationDelay: `${idx * 0.3}s` }} />
              </g>
            );
          })}
        </g>

        {/* Hubs */}
        <g>
          {HUBS.map((h) => (
            <g key={h.id}>
              <circle cx={h.x} cy={h.y} r={h.primary ? 10 : 5} fill="#D4AF37" opacity={h.primary ? 0.18 : 0.12}>
                <animate attributeName="r" values={`${h.primary?6:3};${h.primary?14:8};${h.primary?6:3}`} dur="2.4s" repeatCount="indefinite" />
              </circle>
              <circle cx={h.x} cy={h.y} r={h.primary ? 4 : 2.5} fill="#D4AF37" />
              {h.primary && (
                <text
                  x={h.x + 12}
                  y={h.y + 4}
                  fill="#D4AF37"
                  fontSize="11"
                  fontFamily="Manrope, sans-serif"
                  letterSpacing="2"
                  className="uppercase"
                >
                  {h.name}
                </text>
              )}
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
};

export default WorldMap;
